<template>
    <div class="file-upload-container">
        <el-upload
            ref="upload"
            class="file-uploader"
            :http-request="customUpload"
            :show-file-list="false"
            :accept="accept"
            :disabled="uploading">
            <el-button
                type="primary"
                icon="el-icon-upload2"
                :loading="uploading">
                {{ uploading ? '上传中...' : '选择文件' }}
            </el-button>
        </el-upload>

        <!-- 文件信息展示 -->
        <div v-if="fileInfo.name" class="file-info">
            <el-alert
                :title="fileInfo.name"
                :type="uploadStatus === 'success' ? 'success' : (uploadStatus === 'error' ? 'error' : 'info')"
                :closable="false"
                show-icon>
                <div slot="default" class="file-detail">
                    <div class="file-meta">
                        <span class="file-size">大小: {{ formatFileSize(fileInfo.size) }}</span>
                        <span v-if="fileInfo.progress !== undefined" class="file-progress">
                            进度: {{ fileInfo.progress }}%
                        </span>
                    </div>
                    <div v-if="downloadUrl" class="file-url">
                        <el-link
                            type="primary"
                            :href="downloadUrl"
                            target="_blank"
                            :underline="false">
                            <i class="el-icon-download"></i> 下载文件
                        </el-link>
                        <el-link
                            type="danger"
                            @click="clearFile"
                            :underline="false"
                            style="margin-left: 15px;">
                            <i class="el-icon-delete"></i> 清除
                        </el-link>
                    </div>
                </div>
            </el-alert>
        </div>

        <!-- 提示信息 -->
        <div class="upload-tips" v-if="tips">
            {{ tips }}
        </div>
    </div>
</template>

<script>
import { reqUploadFile } from '@/api/system';

export default {
    name: 'SingleFileUpload',

    props: {
        // 已上传的文件URL（支持v-model）
        value: {
            type: String,
            default: ''
        },
        // 接受的文件类型
        accept: {
            type: String,
            default: '.apk,.ipa,.wgt,.zip,.rar'
        },
        // 文件大小限制（MB）
        maxSize: {
            type: Number,
            default: 500
        },
        // 提示信息
        tips: {
            type: String,
            default: '支持 APK、IPA、WGT、ZIP、RAR 格式，文件大小不超过 500MB'
        },
        // 是否自动上传到服务器
        autoUpload: {
            type: Boolean,
            default: true
        }
    },

    data() {
        return {
            uploading: false,
            uploadStatus: '', // success, error
            fileInfo: {
                name: '',
                size: 0,
                progress: 0
            },
            downloadUrl: this.value || ''
        };
    },

    watch: {
        value(newVal) {
            this.downloadUrl = newVal;
            if (!newVal) {
                this.clearFileInfo();
            }
        }
    },

    methods: {
        // 自定义上传方法
        async customUpload(options) {
            const { file } = options;

            // 检查文件大小（maxSize <= 0 时不限制）
            if (this.maxSize > 0) {
                const isLtMaxSize = file.size / 1024 / 1024 < this.maxSize;
                if (!isLtMaxSize) {
                    this.$message.error(`文件大小不能超过 ${this.maxSize}MB!`);
                    return;
                }
            }

            // 检查文件类型（accept 为空或不限时不限制）
            if (this.accept && this.accept.trim() !== '' && this.accept.trim() !== '*') {
                const fileName = file.name.toLowerCase();
                const allowedTypes = this.accept.split(',').map(type => type.trim().toLowerCase());
                const isValidType = allowedTypes.some(type => fileName.endsWith(type.replace('.', '')));
                if (!isValidType) {
                    this.$message.error(`只能上传 ${this.accept} 格式的文件!`);
                    return;
                }
            }

            // 保存文件信息
            this.fileInfo = {
                name: file.name,
                size: file.size,
                progress: 0
            };

            this.uploading = true;
            this.uploadStatus = '';

            try {
                // 创建 FormData
                const formData = new FormData();
                formData.append('file', file);

                // 调用上传接口，返回的是 { url, key, fileName, fileSize, contentType }
                const result = await reqUploadFile(formData);
                
                // 提取 URL 字符串
                const url = result.url || result;
                
                this.downloadUrl = url;
                this.uploadStatus = 'success';
                this.fileInfo.progress = 100;
                this.uploading = false;
                
                // 触发input事件，支持v-model（传递URL字符串）
                this.$emit('input', url);
                this.$emit('success', { url, file, result });
                
                this.$message.success('文件上传成功');
            } catch (error) {
                this.uploading = false;
                this.uploadStatus = 'error';
                this.$message.error('文件上传失败: ' + (error.message || '未知错误'));
                this.$emit('error', error);
            }
        },

        // 清除文件
        clearFile() {
            this.downloadUrl = '';
            this.clearFileInfo();
            this.$emit('input', '');
            this.$emit('clear');
            this.$message.success('已清除文件');
        },

        // 清除文件信息
        clearFileInfo() {
            this.fileInfo = {
                name: '',
                size: 0,
                progress: 0
            };
            this.uploadStatus = '';
        },

        // 格式化文件大小
        formatFileSize(bytes) {
            if (!bytes || bytes === 0) return '0 B';
            const k = 1024;
            const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return (bytes / Math.pow(k, i)).toFixed(2) + ' ' + sizes[i];
        }
    }
};
</script>

<style scoped>
.file-upload-container {
    width: 100%;
}

.file-uploader {
    display: inline-block;
}

.file-info {
    margin-top: 10px;
}

.file-detail {
    padding: 5px 0;
}

.file-meta {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}

.file-size, .file-progress {
    font-size: 12px;
    color: #606266;
}

.file-url {
    display: flex;
    align-items: center;
}

.upload-tips {
    color: #909399;
    font-size: 12px;
    margin-top: 8px;
    line-height: 1.5;
}

::v-deep .el-alert {
    padding: 10px 12px;
}

::v-deep .el-alert__title {
    font-size: 13px;
    line-height: 1.5;
}
</style>
